import { useEffect, useRef } from "react";
import { useTutorial as useTutorialContext } from "../contexts/TutorialContext";
import { TutorialConfig } from "../types/tutorial";
import { createLogger } from "../utils/logger";
import { useAnalytics } from "./useAnalytics";

const logger = createLogger("useTutorial");

export const useTutorial = () => {
  const tutorialContext = useTutorialContext();
  const analytics = useAnalytics();

  return {
    ...tutorialContext,
    // Enhanced startTutorial with analytics
    startTutorial: (config: TutorialConfig) => {
      logger.info("Starting tutorial:", config.id);

      // Track tutorial start
      analytics.logTutorialEvent("tutorial_started", {
        tutorialId: config.id,
        tutorialName: config.name,
        stepCount: config.steps.length,
      });

      // Start the tutorial
      tutorialContext.startTutorial(config);
    },

    // Enhanced markTutorialSeen with analytics
    markTutorialSeen: (pageId: string) => {
      logger.info("Marking tutorial as seen:", pageId);

      // Track tutorial completion
      analytics.logTutorialEvent("tutorial_completed", {
        tutorialId: pageId,
        completionType: "seen",
      });

      tutorialContext.markTutorialSeen(pageId);
    },

    // Enhanced skipAllTutorials with analytics
    skipAllTutorials: () => {
      logger.info("User skipped all tutorials");

      // Track tutorial skip
      analytics.logTutorialEvent("tutorial_skipped", {
        tutorialId: "all_tutorials",
        skipType: "all_tutorials",
      });

      tutorialContext.skipAllTutorials();
    },
  };
};

// Hook for page-specific tutorial management
export const usePageTutorial = (pageId: string, tutorialConfig: TutorialConfig) => {
  const { hasSeenTutorial, startTutorial, state } = useTutorial();
  const hasAttemptedStart = useRef(false);

  useEffect(() => {
    // logger.info("usePageTutorial effect triggered:", {
    //   pageId,
    //   hasSeenTutorial: hasSeenTutorial(pageId),
    //   skipAllTutorials: state.skipAllTutorials,
    //   isActive: state.isActive,
    //   isLoading: state.isLoading,
    //   tutorialConfigId: tutorialConfig.id,
    //   hasAttemptedStart: hasAttemptedStart.current,
    // });

    // Only start tutorial if:
    // 1. Tutorial context is not loading
    // 2. User hasn't seen this tutorial
    // 3. User hasn't skipped all tutorials
    // 4. No tutorial is currently active
    // 5. We haven't already attempted to start this tutorial
    if (!state.isLoading && !hasSeenTutorial(pageId) && !state.skipAllTutorials && !state.isActive && !hasAttemptedStart.current) {
      logger.info("Starting tutorial automatically for page:", pageId);
      hasAttemptedStart.current = true;
      // Start tutorial immediately when page loads
      startTutorial(tutorialConfig);
    }
    // else {
    // logger.info("Not starting tutorial automatically:", {
    //   reason: state.isLoading
    //     ? "still loading"
    //     : hasSeenTutorial(pageId)
    //       ? "already seen"
    //       : state.skipAllTutorials
    //         ? "all tutorials skipped"
    //         : state.isActive
    //           ? "tutorial already active"
    //           : hasAttemptedStart.current
    //             ? "already attempted start"
    //             : "unknown",
    // });
    // }
  }, [pageId, hasSeenTutorial, startTutorial, state.isActive, state.skipAllTutorials, state.isLoading, tutorialConfig]);

  // Reset the attempt flag when the pageId changes or when tutorial is no longer active
  useEffect(() => {
    hasAttemptedStart.current = false;
  }, [pageId, state.isActive]);

  return {
    hasSeenTutorial: hasSeenTutorial(pageId),
    canStartTutorial: !hasSeenTutorial(pageId) && !state.skipAllTutorials,
    isTutorialActive: state.isActive && state.currentTutorial === pageId,
  };
};
